import React, { useState, useEffect, useRef, useCallback } from "react";
import {
	Box,
	Card,
	CardContent,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	InputAdornment,
	CircularProgress,
	Chip,
	Alert,
	Stack,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TablePagination,
	Button,
} from "@mui/material";
import { Search, Calendar, Repeat, AlertCircle, Download } from "lucide-react";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import * as XLSX from "xlsx";

const DAYS_OF_WEEK = {
	1: "Monday",
	2: "Tuesday",
	3: "Wednesday",
	4: "Thursday",
	5: "Friday",
	6: "Saturday",
};

const DATE_FILTER_OPTIONS = {
	all: "All Dates",
	today: "Today's Schedule",
	this_week: "This Week",
	last_week: "Last Week",
	next_week: "Next Week",
	this_month: "This Month",
	last_month: "Last Month",
	next_month: "Next Month",
};

// Helper function to chunk array into smaller arrays
const chunkArray = (array, chunkSize) => {
	const chunks = [];
	for (let i = 0; i < array.length; i += chunkSize) {
		chunks.push(array.slice(i, i + chunkSize));
	}
	return chunks;
};

// Cache for storing data and avoiding unnecessary refetches
const dataCache = {
	teachersData: null,
	userProfile: null,
	timestamp: null,
	userId: null,
	// Cache for 5 minutes
	TTL: 5 * 60 * 1000,

	isValid(userId) {
		return (
			this.teachersData &&
			this.userProfile &&
			this.timestamp &&
			this.userId === userId &&
			Date.now() - this.timestamp < this.TTL
		);
	},

	set(teachersData, userProfile, userId) {
		this.teachersData = teachersData;
		this.userProfile = userProfile;
		this.userId = userId;
		this.timestamp = Date.now();
	},

	clear() {
		this.teachersData = null;
		this.userProfile = null;
		this.timestamp = null;
		this.userId = null;
	},
};

// State persistence for filters and pagination
const filterState = {
	searchTerm: "",
	scheduleFilter: "all",
	dateFilter: "all",
	page: 0,
	rowsPerPage: 10,

	save(state) {
		this.searchTerm = state.searchTerm;
		this.scheduleFilter = state.scheduleFilter;
		this.dateFilter = state.dateFilter;
		this.page = state.page;
		this.rowsPerPage = state.rowsPerPage;
	},

	restore() {
		return {
			searchTerm: this.searchTerm,
			scheduleFilter: this.scheduleFilter,
			dateFilter: this.dateFilter,
			page: this.page,
			rowsPerPage: this.rowsPerPage,
		};
	},
};

const TeacherSchedule = () => {
	const { user } = useAuth();
	const isMountedRef = useRef(true);
	const hasInitialized = useRef(false);

	// State management - initialize from cache if available
	const [loading, setLoading] = useState(true);
	const [teachersData, setTeachersData] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [userProfile, setUserProfile] = useState(null);

	// Initialize filter states from persisted state
	const restoredState = filterState.restore();
	const [searchTerm, setSearchTerm] = useState(restoredState.searchTerm);
	const [scheduleFilter, setScheduleFilter] = useState(
		restoredState.scheduleFilter
	);
	const [dateFilter, setDateFilter] = useState(restoredState.dateFilter);
	const [page, setPage] = useState(restoredState.page);
	const [rowsPerPage, setRowsPerPage] = useState(restoredState.rowsPerPage);

	// Alert state
	const [alert, setAlert] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	// Track component lifecycle
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Save filter state whenever it changes
	useEffect(() => {
		filterState.save({
			searchTerm,
			scheduleFilter,
			dateFilter,
			page,
			rowsPerPage,
		});
	}, [searchTerm, scheduleFilter, dateFilter, page, rowsPerPage]);

	// Initialize data on component mount
	useEffect(() => {
		if (user && !hasInitialized.current) {
			hasInitialized.current = true;
			initializeData();
		}
	}, [user]);

	// Filter data when filters or data changes
	useEffect(() => {
		if (teachersData.length > 0) {
			filterData();
		}
	}, [searchTerm, scheduleFilter, dateFilter, teachersData]);

	// Reset page when filters change (except when page itself changes)
	useEffect(() => {
		setPage(0);
	}, [searchTerm, scheduleFilter, dateFilter]);

	const initializeData = useCallback(async () => {
		if (!user) return;

		// Check if we have valid cached data
		if (dataCache.isValid(user.id)) {
			setTeachersData(dataCache.teachersData);
			setUserProfile(dataCache.userProfile);
			setLoading(false);
			return;
		}

		// Fetch fresh data
		try {
			await fetchUserProfile();
		} catch (error) {
			console.error("Error initializing data:", error);
			if (isMountedRef.current) {
				setLoading(false);
			}
		}
	}, [user]);

	// Memoized function to get date ranges
	const getDateRange = useCallback((filterType) => {
		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

		switch (filterType) {
			case "today":
				return {
					start: today,
					end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1),
				};
			case "this_week":
				const startOfWeek = new Date(today);
				startOfWeek.setDate(today.getDate() - today.getDay());
				const endOfWeek = new Date(startOfWeek);
				endOfWeek.setDate(startOfWeek.getDate() + 6);
				endOfWeek.setHours(23, 59, 59, 999);
				return { start: startOfWeek, end: endOfWeek };
			case "last_week":
				const lastWeekStart = new Date(today);
				lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
				const lastWeekEnd = new Date(lastWeekStart);
				lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
				lastWeekEnd.setHours(23, 59, 59, 999);
				return { start: lastWeekStart, end: lastWeekEnd };
			case "next_week":
				const nextWeekStart = new Date(today);
				nextWeekStart.setDate(today.getDate() - today.getDay() + 7);
				const nextWeekEnd = new Date(nextWeekStart);
				nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
				nextWeekEnd.setHours(23, 59, 59, 999);
				return { start: nextWeekStart, end: nextWeekEnd };
			case "this_month":
				const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
				const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
				endOfMonth.setHours(23, 59, 59, 999);
				return { start: startOfMonth, end: endOfMonth };
			case "last_month":
				const lastMonthStart = new Date(
					now.getFullYear(),
					now.getMonth() - 1,
					1
				);
				const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
				lastMonthEnd.setHours(23, 59, 59, 999);
				return { start: lastMonthStart, end: lastMonthEnd };
			case "next_month":
				const nextMonthStart = new Date(
					now.getFullYear(),
					now.getMonth() + 1,
					1
				);
				const nextMonthEnd = new Date(now.getFullYear(), now.getMonth() + 2, 0);
				nextMonthEnd.setHours(23, 59, 59, 999);
				return { start: nextMonthStart, end: nextMonthEnd };
			default:
				return null;
		}
	}, []);

	// Helper function to check if a schedule falls within date range
	const isScheduleInDateRange = useCallback((schedule, dateRange) => {
		if (!dateRange || !schedule.startDate || schedule.startDate === "-") {
			return false;
		}

		const scheduleDate = new Date(schedule.startDate);
		return scheduleDate >= dateRange.start && scheduleDate <= dateRange.end;
	}, []);

	const fetchUserProfile = async () => {
		try {
			if (!user || !isMountedRef.current) return;

			const { data, error } = await supabase
				.from("profiles")
				.select("role")
				.eq("id", user.id)
				.single();

			if (error) {
				console.error("Error fetching user profile:", error);
				throw error;
			}

			if (isMountedRef.current) {
				setUserProfile(data);
				// Fetch teachers and schedules after getting user profile
				await fetchTeachersAndSchedules(data);
			}
		} catch (error) {
			console.error("Error fetching user profile:", error);
			if (isMountedRef.current) {
				showAlert("Error fetching user profile", "error");
				setLoading(false);
			}
		}
	};

	// Updated function to fetch schedules in chunks
	const fetchSchedulesInChunks = async (teacherIds) => {
		try {
			// If no teacher IDs, return empty array
			if (!teacherIds || teacherIds.length === 0) {
				return [];
			}

			// Chunk the teacher IDs to avoid URL length issues
			const chunks = chunkArray(teacherIds, 100); // 100 IDs per request
			const allSchedules = [];

			for (const chunk of chunks) {
				if (!isMountedRef.current) break; // Stop if component unmounted

				const { data: schedules, error: schedulesError } = await supabase
					.from("teacher_schedules")
					.select(
						`
						id,
						teacher_id,
						day_of_week,
						period,
						start_date,
						end_date,
						status,
						schedule_type,
						recurring_pattern,
						grade_id
					`
					)
					.in("teacher_id", chunk)
					.eq("status", "active");

				if (schedulesError) {
					console.error("Schedules fetch error for chunk:", schedulesError);
					throw schedulesError;
				}

				allSchedules.push(...(schedules || []));
			}

			return allSchedules;
		} catch (error) {
			console.error("Error fetching schedules in chunks:", error);
			throw error;
		}
	};

	const fetchTeachersAndSchedules = async (profileData) => {
		try {
			if (!isMountedRef.current) return;

			// Get current user's role and filter schools accordingly
			let allowedSchoolIds = null;

			if (
				profileData?.role === "ROLE_FUNMATH_ADMIN" ||
				profileData?.role === "ROLE_FUNMIYO_EMPLOYEE"
			) {
				// For these roles, get schools they own
				const { data: userSchools, error: userSchoolsError } = await supabase
					.from("employee_schools")
					.select("school_id")
					.eq("profile_id", user.id)
					.eq("active_status", true);

				if (userSchoolsError) {
					console.error("User schools fetch error:", userSchoolsError);
					throw userSchoolsError;
				}

				allowedSchoolIds = userSchools?.map((school) => school.school_id) || [];

				// If user has no schools assigned, return empty data
				if (allowedSchoolIds.length === 0) {
					if (isMountedRef.current) {
						setTeachersData([]);
						setLoading(false);
					}
					return;
				}
			}

			// Build teachers query with school filtering if needed
			let teachersQuery = supabase
				.from("profiles")
				.select(
					`
					id,
					full_name,
					email,
					status,
					school_id,
					schools!profiles_school_id_fkey (
						id,
						name,
						district
					)
				`
				)
				.eq("role", "ROLE_SCHOOL_TEACHER")
				.order("full_name");

			// Apply school filtering for non-admin users
			if (allowedSchoolIds !== null) {
				teachersQuery = teachersQuery.in("school_id", allowedSchoolIds);
			}

			if (!isMountedRef.current) return;

			const { data: teachers, error: teachersError } = await teachersQuery;

			// Fetch school ownerships separately for better performance
			let schoolOwnershipsQuery = supabase
				.from("employee_schools")
				.select(
					`
                school_id,
                profile_id,
                active_status,
                profiles:profile_id!inner (
                    id,
                    full_name,
                    role
                )
            `
				)
				.eq("profiles.role", "ROLE_FUNMIYO_EMPLOYEE")
				.eq("active_status", true);

			// Filter school ownerships for non-admin users
			if (allowedSchoolIds !== null) {
				schoolOwnershipsQuery = schoolOwnershipsQuery.in(
					"school_id",
					allowedSchoolIds
				);
			}

			if (!isMountedRef.current) return;

			const { data: schoolOwnerships, error: ownershipError } =
				await schoolOwnershipsQuery;

			if (teachersError) {
				console.error("Teachers fetch error:", teachersError);
				throw teachersError;
			}

			if (ownershipError) {
				console.error("Ownership fetch error:", ownershipError);
				// Continue without ownership data rather than failing
			}

			// Create a map of school owners for quick lookup
			const schoolOwnersMap = {};
			if (schoolOwnerships) {
				schoolOwnerships.forEach((ownership) => {
					if (!schoolOwnersMap[ownership.school_id]) {
						schoolOwnersMap[ownership.school_id] = [];
					}
					schoolOwnersMap[ownership.school_id].push({
						id: ownership.profiles.id,
						full_name: ownership.profiles.full_name,
						role: ownership.profiles.role,
					});
				});
			}

			// Fetch all schedules for these teachers using chunked approach
			const teacherIds = teachers.map((t) => t.id);

			if (!isMountedRef.current) return;

			const activeSchedules = await fetchSchedulesInChunks(teacherIds);

			// Fetch grades separately
			const gradeIds = [
				...new Set(activeSchedules.map((s) => s.grade_id).filter(Boolean)),
			];
			let grades = [];

			if (gradeIds.length > 0 && isMountedRef.current) {
				const { data: gradesData, error: gradesError } = await supabase
					.from("grades")
					.select("id, name")
					.in("id", gradeIds);

				if (gradesError) {
					console.error("Grades fetch error:", gradesError);
				} else {
					grades = gradesData || [];
				}
			}

			if (!isMountedRef.current) return;

			// Process the data to create a flat structure for the table
			const processedData = [];

			teachers.forEach((teacher) => {
				const teacherSchedules = activeSchedules.filter(
					(schedule) => schedule.teacher_id === teacher.id
				);

				if (teacherSchedules.length === 0) {
					// Teacher with no schedules
					processedData.push({
						teacherId: teacher.id,
						teacherName: teacher.full_name || "Unnamed Teacher",
						teacherEmail: teacher.email,
						teacherStatus: teacher.status,
						schoolName: teacher.schools?.name || "No School Assigned",
						district: teacher.schools?.district || "-",
						owner: getSchoolOwners(teacher.schools?.id, schoolOwnersMap),
						hasSchedule: false,
						scheduleId: null,
						day: "-",
						period: "-",
						gradeName: "-",
						scheduleType: "-",
						startDate: "-",
						endDate: "-",
						rawStartDate: null,
						rawEndDate: null,
						isRecurring: false,
					});
				} else {
					// Teacher with schedules - create a row for each schedule
					teacherSchedules.forEach((schedule) => {
						const grade = grades.find((g) => g.id === schedule.grade_id);
						processedData.push({
							teacherId: teacher.id,
							teacherName: teacher.full_name || "Unnamed Teacher",
							teacherEmail: teacher.email,
							teacherStatus: teacher.status,
							schoolName: teacher.schools?.name || "No School Assigned",
							district: teacher.schools?.district || "-",
							owner: getSchoolOwners(teacher.schools?.id, schoolOwnersMap),
							hasSchedule: true,
							scheduleId: schedule.id,
							day: DAYS_OF_WEEK[schedule.day_of_week] || "-",
							period: schedule.period || "-",
							gradeName: grade?.name || "Unknown Grade",
							scheduleType: schedule.schedule_type || "single",
							startDate: schedule.start_date
								? new Date(schedule.start_date).toLocaleDateString()
								: "-",
							endDate: schedule.end_date
								? new Date(schedule.end_date).toLocaleDateString()
								: "-",
							rawStartDate: schedule.start_date,
							rawEndDate: schedule.end_date,
							isRecurring: schedule.schedule_type === "recurring",
							recurringPattern: schedule.recurring_pattern,
						});
					});
				}
			});

			if (isMountedRef.current) {
				setTeachersData(processedData);
				// Cache the data
				dataCache.set(processedData, profileData, user.id);
			}
		} catch (error) {
			console.error("Error fetching teacher schedules:", error);
			if (isMountedRef.current) {
				showAlert("Error fetching teacher schedules", "error");
			}
		} finally {
			if (isMountedRef.current) {
				setLoading(false);
			}
		}
	};

	// Function to get school owners from the employee_schools table
	const getSchoolOwners = (schoolId, schoolOwnersMap) => {
		if (!schoolId || !schoolOwnersMap || !schoolOwnersMap[schoolId]) {
			return "No owners assigned";
		}

		const owners = schoolOwnersMap[schoolId];

		if (owners.length === 0) {
			return "No owners assigned";
		}

		// If only one owner, show their name
		if (owners.length === 1) {
			return owners[0].full_name;
		}

		// If multiple owners, show first name + count
		return `${owners[0].full_name} +${owners.length - 1} more`;
	};

	const filterData = useCallback(() => {
		let filtered = [...teachersData];

		// Apply search filter across all data (not just current page)
		if (searchTerm.trim()) {
			const searchLower = searchTerm.toLowerCase();
			filtered = filtered.filter(
				(row) =>
					row.teacherName.toLowerCase().includes(searchLower) ||
					row.teacherEmail.toLowerCase().includes(searchLower) ||
					row.schoolName.toLowerCase().includes(searchLower) ||
					row.district.toLowerCase().includes(searchLower) ||
					row.owner.toLowerCase().includes(searchLower) ||
					row.gradeName.toLowerCase().includes(searchLower) ||
					row.day.toLowerCase().includes(searchLower)
			);
		}

		// Apply schedule filter
		if (scheduleFilter === "with_schedule") {
			filtered = filtered.filter((row) => row.hasSchedule);
		} else if (scheduleFilter === "without_schedule") {
			filtered = filtered.filter((row) => !row.hasSchedule);
		}

		// Apply date filter
		if (dateFilter !== "all") {
			const dateRange = getDateRange(dateFilter);
			if (dateRange) {
				filtered = filtered.filter((row) => {
					if (!row.hasSchedule) {
						return false; // Don't show teachers without schedules for date-specific filters
					}
					return isScheduleInDateRange(row, dateRange);
				});
			}
		}

		setFilteredData(filtered);
	}, [
		teachersData,
		searchTerm,
		scheduleFilter,
		dateFilter,
		getDateRange,
		isScheduleInDateRange,
	]);

	const handleDownloadExcel = useCallback(() => {
		try {
			// Prepare data for Excel export
			const excelData = filteredData.map((row, index) => ({
				"S.No": index + 1,
				"Teacher Name": row.teacherName,
				Email: row.teacherEmail,
				School: row.schoolName,
				Owner: row.owner,
				District: row.district,
				Status: row.teacherStatus,
				Day: row.day,
				Period: row.period === "-" ? row.period : `Period ${row.period}`,
				Grade: row.gradeName,
				"Schedule Type": row.scheduleType,
				"Start Date": row.startDate,
				"End Date": row.endDate,
				"Is Recurring": row.isRecurring ? "Yes" : "No",
			}));

			// Create workbook and worksheet
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.json_to_sheet(excelData);

			// Set column widths
			const colWidths = [
				{ wch: 8 }, // S.No
				{ wch: 25 }, // Teacher Name
				{ wch: 30 }, // Email
				{ wch: 30 }, // School
				{ wch: 25 }, // Owner
				{ wch: 20 }, // District
				{ wch: 15 }, // Status
				{ wch: 12 }, // Day
				{ wch: 10 }, // Period
				{ wch: 15 }, // Grade
				{ wch: 15 }, // Schedule Type
				{ wch: 12 }, // Start Date
				{ wch: 12 }, // End Date
				{ wch: 12 }, // Is Recurring
			];
			ws["!cols"] = colWidths;

			// Add worksheet to workbook
			XLSX.utils.book_append_sheet(wb, ws, "Teacher Schedules");

			// Generate filename with current date and filter info
			const today = new Date().toISOString().split("T")[0];
			let filename = `teacher_schedules_${today}`;

			if (dateFilter !== "all") {
				filename += `_${dateFilter}`;
			}
			if (scheduleFilter !== "all") {
				filename += `_${scheduleFilter}`;
			}
			filename += ".xlsx";

			// Download file
			XLSX.writeFile(wb, filename);

			showAlert("Excel file downloaded successfully", "success");
		} catch (error) {
			console.error("Error downloading Excel file:", error);
			showAlert("Error downloading Excel file", "error");
		}
	}, [filteredData, dateFilter, scheduleFilter]);

	const showAlert = useCallback((message, severity = "success") => {
		if (isMountedRef.current) {
			setAlert({
				open: true,
				message,
				severity,
			});
		}
	}, []);

	const handleChangePage = useCallback((event, newPage) => {
		setPage(newPage);
	}, []);

	const handleChangeRowsPerPage = useCallback((event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	}, []);

	// Get current page data
	const getCurrentPageData = useCallback(() => {
		const startIndex = page * rowsPerPage;
		const endIndex = startIndex + rowsPerPage;
		return filteredData.slice(startIndex, endIndex);
	}, [filteredData, page, rowsPerPage]);

	// Force refresh function - clears cache and refetches
	const handleRefresh = useCallback(async () => {
		dataCache.clear();
		hasInitialized.current = false;
		setLoading(true);
		await initializeData();
	}, [initializeData]);

	if (loading) {
		return (
			<Box
				sx={{
					p: 2,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: 300,
				}}>
				<Stack spacing={2} alignItems="center">
					<CircularProgress />
					<Typography color="text.secondary">
						Loading teacher schedules...
					</Typography>
				</Stack>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 2 }}>
			{/* Header */}
			<Stack spacing={3} sx={{ mb: 3 }}>
				<Stack
					direction="row"
					alignItems="center"
					spacing={2}
					justifyContent="space-between">
					<Stack direction="row" alignItems="center" spacing={2}>
						<Calendar size={24} color="#6366f1" />
						<Typography variant="h5" fontWeight="bold">
							Teacher Schedules
						</Typography>
					</Stack>
					<Button
						variant="outlined"
						onClick={handleRefresh}
						size="small"
						sx={{ minWidth: 100 }}>
						Refresh
					</Button>
				</Stack>

				{/* Search, Filters, and Download */}
				<Card>
					<CardContent sx={{ pb: 2 }}>
						<Stack
							direction="row"
							spacing={2}
							alignItems="center"
							sx={{ flexWrap: { xs: "wrap", lg: "nowrap" } }}>
							<TextField
								placeholder="Search by teacher name, email, school, district, owner, grade, or day..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Search size={20} />
										</InputAdornment>
									),
								}}
								size="small"
								sx={{ flex: 1, minWidth: 240 }}
							/>

							<FormControl size="small" sx={{ minWidth: 180 }}>
								<InputLabel>Filter by Schedule</InputLabel>
								<Select
									value={scheduleFilter}
									label="Filter by Schedule"
									onChange={(e) => setScheduleFilter(e.target.value)}>
									<MenuItem value="all">All Teachers</MenuItem>
									<MenuItem value="with_schedule">With Schedules</MenuItem>
									<MenuItem value="without_schedule">
										Without Schedules
									</MenuItem>
								</Select>
							</FormControl>

							<FormControl size="small" sx={{ minWidth: 160 }}>
								<InputLabel>Filter by Date</InputLabel>
								<Select
									value={dateFilter}
									label="Filter by Date"
									onChange={(e) => setDateFilter(e.target.value)}>
									{Object.entries(DATE_FILTER_OPTIONS).map(([key, value]) => (
										<MenuItem key={key} value={key}>
											{value}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<Button
								variant="contained"
								startIcon={<Download size={20} />}
								onClick={handleDownloadExcel}
								sx={{
									minWidth: 140,
									whiteSpace: "nowrap",
									flexShrink: 0,
									bgcolor: "#6366f1",
									"&:hover": {
										bgcolor: "#5856eb",
									},
								}}>
								Download Excel
							</Button>
						</Stack>
					</CardContent>
				</Card>
			</Stack>

			{/* Results Summary */}
			<Box
				sx={{
					mb: 2,
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}>
				<Typography variant="body2" color="text.secondary">
					Showing {getCurrentPageData().length} of {filteredData.length}{" "}
					schedule entries
					{(searchTerm || scheduleFilter !== "all" || dateFilter !== "all") && (
						<span> (filtered from {teachersData.length} total)</span>
					)}
					{userProfile?.role !== "ROLE_FUNMIYO_ADMIN" && (
						<span> • Showing data for your assigned schools only</span>
					)}
					{dataCache.isValid(user?.id) && <span> • Cached data</span>}
				</Typography>
			</Box>

			{/* Teachers Schedule Table */}
			{filteredData.length === 0 ? (
				<Card>
					<CardContent sx={{ textAlign: "center", py: 6 }}>
						<AlertCircle
							size={48}
							color="#9ca3af"
							style={{ marginBottom: 16 }}
						/>
						<Typography variant="h6" color="text.secondary" gutterBottom>
							No teacher schedules found
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{searchTerm || scheduleFilter !== "all" || dateFilter !== "all"
								? "Try adjusting your search terms or filters"
								: userProfile?.role !== "ROLE_FUNMIYO_ADMIN"
								? "No teacher schedules available for your assigned schools"
								: "No teacher schedules available"}
						</Typography>
					</CardContent>
				</Card>
			) : (
				<Card>
					<TableContainer component={Paper}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: "bold" }}>Email</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>School</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>Owner</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>District</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>Day</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>Period</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>Grade</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>
										Schedule Type
									</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>Start Date</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>End Date</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{getCurrentPageData().map((row, index) => (
									<TableRow
										key={`${row.teacherId}-${
											row.scheduleId || "no-schedule"
										}-${index}`}>
										<TableCell>
											<Typography
												variant="body2"
												fontWeight="medium"
												color={!row.hasSchedule ? "error.main" : "inherit"}>
												{row.teacherName}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={
													!row.hasSchedule ? "error.main" : "text.secondary"
												}>
												{row.teacherEmail}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={!row.hasSchedule ? "error.main" : "inherit"}>
												{row.schoolName}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={
													!row.hasSchedule ? "error.main" : "text.secondary"
												}>
												{row.owner}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={
													!row.hasSchedule ? "error.main" : "text.secondary"
												}>
												{row.district}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												size="small"
												label={row.teacherStatus}
												color={
													row.teacherStatus === "Approved"
														? "success"
														: "warning"
												}
												variant="outlined"
											/>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={!row.hasSchedule ? "error.main" : "inherit"}>
												{!row.hasSchedule ? "No schedule created" : row.day}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={!row.hasSchedule ? "error.main" : "inherit"}>
												{row.period !== "-" && row.hasSchedule
													? `Period ${row.period}`
													: row.period}
											</Typography>
										</TableCell>
										<TableCell>
											{row.hasSchedule ? (
												<Chip
													size="small"
													label={row.gradeName}
													color="primary"
													variant="outlined"
												/>
											) : (
												<Typography variant="body2" color="error.main">
													{row.gradeName}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Stack direction="row" alignItems="center" spacing={1}>
												{row.isRecurring && (
													<Repeat size={16} color="#6366f1" />
												)}
												<Typography
													variant="body2"
													sx={{ textTransform: "capitalize" }}
													color={!row.hasSchedule ? "error.main" : "inherit"}>
													{row.scheduleType}
												</Typography>
											</Stack>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={!row.hasSchedule ? "error.main" : "inherit"}>
												{row.startDate}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												color={!row.hasSchedule ? "error.main" : "inherit"}>
												{row.endDate}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					{/* Pagination */}
					<Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
						<TablePagination
							component="div"
							count={filteredData.length}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[5, 10, 25, 50, 100]}
						/>
					</Box>
				</Card>
			)}

			{/* Alert Snackbar */}
			{alert.open && (
				<Alert
					severity={alert.severity}
					onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
					sx={{ mt: 2 }}>
					{alert.message}
				</Alert>
			)}
		</Box>
	);
};

export default TeacherSchedule;
